/**
 * Default workflows (M26 P6, DEC-099): the workflows every workspace starts with. Mirrors backend
 * `app/core/constants/workflow_defaults.py`; the catalogue (names, descriptions, definitions) is served by
 * the API, never copied here.
 */

/** Who wrote a workflow: the workspace, or Sagot (a default: not deletable, its trigger cannot change). */
export const WORKFLOW_ORIGINS = ["user", "system"] as const;
export type WorkflowOrigin = (typeof WORKFLOW_ORIGINS)[number];

/** Where a workflow runs: after the event committed (every workspace workflow), or at the call site of the event (a default only). */
export const WORKFLOW_EXECUTIONS = ["async", "inline"] as const;
export type WorkflowExecution = (typeof WORKFLOW_EXECUTIONS)[number];

/** The stable key of each default: the 15 that start live, then the 2 optional ones that start paused. */
export const DEFAULT_WORKFLOW_KEYS = [
  "handoff_route_and_alert",
  "handoff_reminder",
  "handoff_escalation",
  "handoff_waiting_digest",
  "assignment_alert",
  "returned_to_queue_alert",
  "new_lead_route_and_alert",
  "returning_lead_alert",
  "lead_owner_changed_alert",
  "booking_requested_alert",
  "booking_confirmed_alert",
  "source_processed_alert",
  "source_failed_alert",
  "gap_assigned_alert",
  "developer_webhooks",
  "negative_feedback_alert",
  "sla_breach_alert",
  // Service Desk (M27 S1): provisioned with the module, not with every workspace.
  "ticket_route_and_alert",
  "ticket_assignment_alert",
  "ticket_received_client",
  "ticket_reply_client",
  "ticket_status_client",
  "ticket_sla_breach_alert",
  "ticket_unassigned_escalation",
  "ticket_waiting_reminder",
  "ticket_auto_close",
  "ticket_csat_request",
  "ticket_low_csat_alert",
  "ticket_client_replied_alert",
  "concern_intake",
  "handoff_to_ticket",
  "ticket_sla_warning_alert",
  "ticket_pending_auto_resolve",
  "ticket_returned_to_queue_alert",
  "ticket_mention_alert",
  "order_placed_alert",
  "order_approval_request",
  "order_status_client",
  "quote_ready_client",
  "order_approval_result",
  "order_sync_failed_alert",
  // Field service (M27 S3): WD-13, WD-14, WD-15 live; WD-22, WD-23, WD-24 start paused.
  "visit_scheduled_client",
  "visit_reminders",
  "visit_report_client",
  "maintenance_due_ticket",
  "warranty_expiring_notice",
  "contract_renewal_ticket",
] as const;
export type DefaultWorkflowKey = (typeof DEFAULT_WORKFLOW_KEYS)[number];

/** The defaults that start paused (the alert they describe does not exist today). */
export const OPTIONAL_DEFAULT_WORKFLOW_KEYS: ReadonlySet<DefaultWorkflowKey> = new Set(["negative_feedback_alert", "sla_breach_alert", "handoff_to_ticket", "order_approval_request", "order_status_client", "order_approval_result", "order_sync_failed_alert", "maintenance_due_ticket", "warranty_expiring_notice", "contract_renewal_ticket"]);

/** The sub-headings of the Default workflows section, in order. */
export const DEFAULT_WORKFLOW_AREAS = ["conversations", "leads", "knowledge", "developers", "service_desk", "orders_field_service"] as const;
export type DefaultWorkflowArea = (typeof DEFAULT_WORKFLOW_AREAS)[number];

/** Triggers only a default workflow listens to; they join the trigger picker when the steps behind them exist. */
export const SYSTEM_ONLY_TRIGGERS = [
  "platform_event",
  "handoff_reminder_due",
  "handoff_escalation_due",
  "handoff_waiting_digest_due",
  "conversation_returned_to_queue",
  "lead_followed_up",
  "gap_assigned",
] as const;
export type SystemOnlyTrigger = (typeof SYSTEM_ONLY_TRIGGERS)[number];

/** Steps a default may hold on every plan; a duplicate has no limit. At most this many paths and steps per path. */
export const SYSTEM_STEP_ALLOWLIST: ReadonlySet<string> = new Set([
  "notify_team",
  "route_handoff",
  "assign_lead_owner",
  "send_to_subscribed_endpoints",
  "branch",
  "end",
  "add_tags",
  "remove_tags",
  "add_note",
  "set_priority",
  "assign",
  "route_ticket",
  "assign_ticket",
  "notify_contact",
  "set_ticket_state",
  "add_ticket_note",
  "request_csat",
  "create_ticket",
  "link_ticket",
  "update_ticket",
  // after-sales: find an asset, propose a visit (M27 S3)
  "lookup_asset",
  "schedule_visit",
  // orders: search the catalogue, read an order status (M27 S2)
  "lookup_catalogue",
  "check_order_status",
  // the customer-facing steps of the concern intake (WD-20)
  "let_assistant_answer",
  "reply_buttons",
  "send_message",
  "ticket_intake",
]);
export const SYSTEM_WORKFLOW_LIMITS = { paths: 3, stepsPerPath: 6 } as const;
/** A default on a customer-facing trigger (the concern intake, WD-20) may hold one path more: "that solved it". */
export const SYSTEM_FACING_MAX_PATHS = 4;

/** `error.reason` of the refusals the console reacts to. Mirrors backend `workflow_default_api.py`. */
export const WORKFLOW_DEFAULT_REASONS = {
  /** A default's trigger cannot change (422). */
  systemTrigger: "system_trigger",
  /** A workflow's trigger cannot change (422). */
  triggerLocked: "trigger_locked",
  /** A default cannot be deleted (409). */
  systemWorkflow: "system_workflow",
  /** Restore default on a workflow that is not a default (409). */
  notADefault: "not_a_default",
  /** The draft holds changes that are not published; restore and roll back need `force` (409). */
  unpublishedChanges: "unpublished_changes",
} as const;
export type WorkflowDefaultReason = (typeof WORKFLOW_DEFAULT_REASONS)[keyof typeof WORKFLOW_DEFAULT_REASONS];

/** What "Restore default" puts back: the baseline, or the newest catalogue revision (which becomes the new baseline). */
export const RESTORE_TARGETS = ["baseline", "latest"] as const;
export type RestoreTarget = (typeof RESTORE_TARGETS)[number];

/** The side of a version comparison that names a default's baseline (`GET /v1/workflows/{id}/diff?base=baseline`). */
export const BASELINE_SIDE = "baseline";

/** The window of the pause dialog's "In the last N days this sent ..." numbers. */
export const IMPACT_DAYS = 30;
